import { useCallback, useEffect, useState } from 'react'
import { Lock } from 'lucide-react'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/clientWeb/components/ui/dialog'
import { Button } from '@/clientWeb/components/ui/button'
import { Input } from '@/clientWeb/components/ui/input'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

export interface MeetingPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  meetingTitle?: string
  /** Called with the entered password. Should resolve to `true` on success,
   * `false` (or throw with a `message`) on failure. */
  onSubmit: (password: string) => Promise<boolean | { ok: false; message: string }>
  onCancel?: () => void
  initialError?: string
  busy?: boolean
}

/**
 * Reusable password gate. Owns its own input state + error display; defers
 * the actual verification to the caller (which talks to meetingsService).
 *
 * Closes automatically on a successful submit. On failure, the message is
 * shown inline and the input is re-focused.
 */
export default function MeetingPasswordDialog({
  open,
  onOpenChange,
  meetingTitle,
  onSubmit,
  onCancel,
  initialError,
  busy,
}: MeetingPasswordDialogProps) {
  const t = useTranslation()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | undefined>(initialError)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setPassword('')
      setError(initialError)
      setSubmitting(false)
    }
  }, [open, initialError])

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()
      if (!password.trim()) {
        setError(t('meetings.enterMeetingPassword'))
        return
      }
      setSubmitting(true)
      try {
        const result = await onSubmit(password)
        if (result === true) {
          toast.success({ title: t('meetings.passwordAccepted'), description: meetingTitle })
          onOpenChange(false)
          return
        }
        if (result && typeof result === 'object' && result.ok === false) {
          setError(result.message)
          return
        }
        setError(t('meetings.errors.INVALID_PASSWORD'))
      } finally {
        setSubmitting(false)
      }
    },
    [password, onSubmit, onOpenChange],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] sm:max-w-[420px] p-0 overflow-hidden bg-card dark:bg-card border-0 shadow-2xl"
        style={{ borderRadius: '16px' }}
        // A stray click beside the field is not a "no". Callers treat any close
        // as leaving — MeetingGuard sends the joiner to /meetings, which for a
        // signed-out participant is the sign-in page — so the gate closes only
        // on Cancel, the X, Escape or a correct password.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-4 sm:p-5 border-b border-stroke-100 dark:border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base sm:text-lg font-semibold text-slate-800 dark:text-white">
                {t('meetings.passwordRequired')}
              </DialogTitle>
              {meetingTitle && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {meetingTitle}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('meetings.passwordRequiredDesc')}
          </p>

          <Input
            autoFocus
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) setError(undefined)
            }}
            placeholder={t('meetings.enterPassword')}
            variant="outlined"
            error={!!error}
            errorText={error}
          />

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onCancel?.()
                onOpenChange(false)
              }}
              disabled={submitting || busy}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || busy || !password.trim()}
              className="flex-1 bg-primary hover:bg-primary/90 text-white"
            >
              {submitting || busy ? t('common.verifying') : t('meetings.join')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
